import { ElementRef, Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';
import { TourStepId } from '../models/enums/tour-step-id.enum';
import { TourPopoverPosition } from '../models/types/tour-popover-position.type';

interface TourStep {
  title: string;
  text: string;
  elementRef: ElementRef<HTMLElement>;
  popoverPosition: TourPopoverPosition;
}

interface RegisteredStep {
  elementRef: ElementRef<HTMLElement>;
  popoverPosition: TourPopoverPosition;
}

interface StepPrepare {
  showDelay?: number;
  beforeShow: () => void;
  afterHide?: () => void;
}

interface StepConfig {
  name: TourStepId;
  title: string;
  text: string;
  position: TourPopoverPosition;
}

const TOUR_STEPS: readonly StepConfig[] = [
  {
    name: TourStepId.Welcome,
    title: 'خوش آمدید!',
    text: 'به مدرسه مدیریتی افرا خوش آمدید! این تور کوتاه شما را با بخش‌های اصلی آشنا می‌کند. برای ادامه «بعدی» را بزنید.',
    position: 'bottom',
  },
  {
    name: TourStepId.NotificationsBell,
    title: 'اعلان‌ها و پیام‌ها',
    text: 'با انتخاب زنگوله، آخرین اعلان‌ها و پیام‌های افرا را مشاهده کنید.',
    position: 'bottom',
  },
  {
    name: TourStepId.UserProfile,
    title: 'پروفایل کاربری',
    text: 'اینجا می‌توانید اطلاعات پروفایل، سمت و جزئیات تماس خود را ببینید.',
    position: 'bottom',
  },
  {
    name: TourStepId.Sidebar,
    title: 'منوی اصلی',
    text: 'این منوی اصلی است. از اینجا می‌توانید به تمام بخش‌های پورتال دسترسی پیدا کنید.',
    position: 'left',
  },
  {
    name: TourStepId.UpcomingWorkshops,
    title: 'کارگاه‌های پیش‌رو',
    text: 'در این بخش، کارگاه‌های آموزشی پیش‌روی خود را ببینید و برای شرکت در آن‌ها ثبت‌نام کنید.',
    position: 'top',
  },
  {
    name: TourStepId.MonthSwitch,
    title: 'جابه‌جایی بین ماه‌ها',
    text: 'با استفاده از این زبانه‌ها می‌توانید بین ماه جاری و دو ماه آینده جابه‌جا شوید و کارگاه‌های هر ماه را به‌تفکیک مشاهده کنید.',
    position: 'bottom',
  },
  {
    name: TourStepId.VideoWorkshops,
    title: 'ویدیوهای آموزشی',
    text: 'ویدیو کارگاه‌های آموزشی گذشته را اینجا مرور و در هر زمان تماشا کنید.',
    position: 'top',
  },
  {
    name: TourStepId.Magazine,
    title: 'مجله افرا',
    text: 'جدیدترین مقالات و اخبار مدرسه مدیریتی افرا را در مجله افرا دنبال کنید.',
    position: 'top',
  },
  {
    name: TourStepId.PortalGuide,
    title: 'راهنمای پورتال',
    text: 'هر زمان نیاز به راهنمایی داشتید، از اینجا می‌توانید این تور را دوباره مشاهده کنید.',
    position: 'left',
  },
  {
    name: TourStepId.SupportLogout,
    title: 'پشتیبانی و خروج',
    text: 'نیاز به کمک دارید؟ از اینجا با پشتیبانی تماس بگیرید. همچنین می‌توانید از سیستم خارج شوید. تور به پایان رسید، از استفاده از پورتال لذت ببرید!',
    position: 'left',
  },
];

@Injectable({ providedIn: 'root' })
export class TourService {
  private readonly STORAGE_PREFIX = 'trainingPortalTourCompleted_';

  private readonly stepPrepares = new Map<TourStepId, StepPrepare>();
  private readonly registeredSteps = new Map<TourStepId, RegisteredStep[]>();
  private readonly stepConfigs: ReadonlyMap<TourStepId, StepConfig> = new Map(
    TOUR_STEPS.map((step) => [step.name, step] as [TourStepId, StepConfig]),
  );

  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);

  private activeIndex = 0;
  private pendingTimeout: ReturnType<typeof setTimeout> | null = null;

  readonly currentIndex = signal(0);
  readonly isActive = signal(false);
  readonly currentStep = signal<TourStep | null>(null);

  readonly totalSteps = TOUR_STEPS.length;

  get isMobile(): boolean {
    return window.innerWidth < 768;
  }

  registerStep(
    stepId: TourStepId,
    elementRef: ElementRef<HTMLElement>,
    popoverPosition?: TourPopoverPosition,
  ): void {
    const config = this.stepConfigs.get(stepId);

    if (!config) return;

    const steps = this.registeredSteps.get(stepId) || [];

    steps.push({
      elementRef,
      popoverPosition: popoverPosition ?? config.position,
    });

    this.registeredSteps.set(stepId, steps);
  }

  registerStepCallbacks(stepId: TourStepId, callbacks: StepPrepare): void {
    this.stepPrepares.set(stepId, {
      showDelay: callbacks.showDelay ?? 400,
      beforeShow: callbacks.beforeShow,
      afterHide: callbacks.afterHide,
    });
  }

  unregisterStep(
    stepId: TourStepId,
    elementRef: ElementRef<HTMLElement>,
  ): void {
    const steps = this.registeredSteps.get(stepId);

    if (!steps) return;

    const stepIndex = steps.findIndex((step) => step.elementRef === elementRef);

    if (stepIndex >= 0) steps.splice(stepIndex, 1);
    if (steps.length === 0) this.registeredSteps.delete(stepId);
  }

  private getStorageKey(): string {
    const user = this.authService.user.value;

    if (!user?.token) return `${this.STORAGE_PREFIX}anonymous`;

    try {
      const payload = JSON.parse(atob(user.token.split('.')[1]));
      const id = payload.user_id || payload.sub || payload.id || 'anonymous';

      return `${this.STORAGE_PREFIX}${id}`;
    } catch {
      return `${this.STORAGE_PREFIX}anonymous`;
    }
  }

  shouldShowTour(): boolean {
    return localStorage.getItem(this.getStorageKey()) !== 'true';
  }

  private findVisibleStep(stepId: TourStepId): RegisteredStep | null {
    const steps = this.registeredSteps.get(stepId);

    if (!steps?.length) return null;

    return (
      steps.find((step) => {
        const rect = step.elementRef.nativeElement.getBoundingClientRect();

        return (
          rect.width > 0 &&
          rect.height > 0 &&
          rect.bottom > 0 &&
          rect.top < window.innerHeight
        );
      }) ?? steps[0]
    );
  }

  private isVisible(step: RegisteredStep): boolean {
    const rect = step.elementRef.nativeElement.getBoundingClientRect();

    return (
      rect.width > 0 &&
      rect.height > 0 &&
      rect.bottom > 0 &&
      rect.top < window.innerHeight
    );
  }

  private clearPending(): void {
    if (this.pendingTimeout) {
      clearTimeout(this.pendingTimeout);

      this.pendingTimeout = null;
    }
  }

  startTour(): void {
    // Wait one microtask after navigation so Angular finishes rendering
    // the home route before we query registered DOM elements.
    this.router
      .navigateByUrl('/')
      .then(() => setTimeout(() => this.initTour()));
  }

  private initTour(): void {
    this.activeIndex = 0;

    this.isActive.set(true);

    this.showStep();
  }

  showNextStep(): void {
    if (this.activeIndex < TOUR_STEPS.length - 1) {
      this.callAfterHide(this.activeIndex + 1);
      this.activeIndex++;

      this.showStep();
    }
  }

  showPreviousStep(): void {
    if (this.activeIndex > 0) {
      this.callAfterHide(this.activeIndex - 1);
      this.activeIndex--;

      this.showStep();
    }
  }

  endTour(): void {
    this.callAfterHide();
    this.clearPending();

    this.isActive.set(false);
    this.currentStep.set(null);

    localStorage.setItem(this.getStorageKey(), 'true');
  }

  private callAfterHide(nextIndex?: number): void {
    const stepId = TOUR_STEPS[this.activeIndex].name;

    if (!stepId) return;

    const prepare = this.stepPrepares.get(stepId);

    if (!prepare?.afterHide) return;

    if (nextIndex !== undefined) {
      const nextStepId = TOUR_STEPS[nextIndex].name;
      const nextPrepare = nextStepId
        ? this.stepPrepares.get(nextStepId)
        : undefined;

      if (prepare.beforeShow === nextPrepare?.beforeShow) return;
    }

    prepare.afterHide();
  }

  private showStep(): void {
    this.clearPending();

    const stepId = TOUR_STEPS[this.activeIndex].name;
    const steps = this.registeredSteps.get(stepId) ?? [];
    const prepare = this.stepPrepares.get(stepId);

    if (!prepare) {
      this.tryShowStep(stepId);
      return;
    }

    const alreadyVisible = steps.some((step) => this.isVisible(step));

    if (alreadyVisible) {
      this.tryShowStep(stepId);
      return;
    }

    prepare.beforeShow();

    this.pendingTimeout = setTimeout(
      () => this.tryShowStep(stepId),
      prepare.showDelay,
    );
  }

  private tryShowStep(stepId: TourStepId, retry = 0): void {
    const config = this.stepConfigs.get(stepId);
    const registered = this.findVisibleStep(stepId);

    if (!config || !registered) {
      if (this.activeIndex < TOUR_STEPS.length - 1) {
        this.activeIndex++;
        this.showStep();
      } else {
        this.endTour();
      }

      return;
    }

    const rect = registered.elementRef.nativeElement.getBoundingClientRect();

    if ((rect.width === 0 || rect.height === 0) && retry < 5) {
      this.pendingTimeout = setTimeout(
        () => this.tryShowStep(stepId, retry + 1),
        100,
      );

      return;
    }

    const popoverPosition =
      this.isMobile &&
      (registered.popoverPosition === 'left' ||
        registered.popoverPosition === 'right')
        ? 'top'
        : registered.popoverPosition;

    this.currentIndex.set(this.activeIndex);
    this.currentStep.set({
      elementRef: registered.elementRef,
      title: config.title,
      text: config.text,
      popoverPosition,
    });
  }
}
